<script setup lang="ts">
interface RunCalculationStatus {
  currentLabel: string;
  currentCalculation: number;
  totalCalculations: number;
  remainingCalculations: number;
}

// runButtonLabel lets the Intermediate Order grid reuse these actions while
// keeping the page-specific wording. Default is the PO Recommendations copy.
withDefaults(
  defineProps<{
    isTestMode: boolean;
    isRunning: boolean;
    isPreparingCustomRequestDialog: boolean;
    selectedPoCount: number;
    runCalculationStatus: RunCalculationStatus | undefined;
    runButtonLabel?: string;
  }>(),
  { runButtonLabel: "⚡ Run Redistribution Algorithm" }
);

const emit = defineEmits<{
  runAlgorithm: [];
  openCustomRequestBodyDialog: [];
}>();
</script>

<template>
  <div class="flex w-[15.625rem] flex-col gap-2">
    <div
      v-if="runCalculationStatus"
      class="run-status-panel"
      role="status"
      aria-live="polite"
    >
      <div class="run-status-panel-header">
        <span class="run-status-panel-spinner" aria-hidden="true">⏳</span>
        <span class="run-status-panel-title">Processing Redistribution</span>
      </div>
      <div class="run-status-panel-progress">
        <div class="run-status-panel-progress-bar">
          <div
            class="run-status-panel-progress-fill"
            :style="{
              width: `${(runCalculationStatus.currentCalculation / runCalculationStatus.totalCalculations) * 100}%`,
            }"
          ></div>
        </div>
        <span class="run-status-panel-step-label">
          Step {{ runCalculationStatus.currentCalculation }} of
          {{ runCalculationStatus.totalCalculations }}
        </span>
      </div>
      <p class="run-status-panel-current-op">
        {{ runCalculationStatus.currentLabel }}
      </p>
      <p class="run-status-panel-remaining">
        {{ runCalculationStatus.remainingCalculations }} step(s) remaining
      </p>
    </div>

    <button
      v-if="isTestMode"
      class="panel-secondary-button w-full"
      type="button"
      :disabled="isRunning || isPreparingCustomRequestDialog"
      @click="emit('openCustomRequestBodyDialog')"
    >
      {{
        isPreparingCustomRequestDialog
          ? "Preparing Custom Request..."
          : "⚡ Run Custom Redistribution"
      }}
    </button>

    <button
      class="results-panel-run-button w-full"
      :disabled="selectedPoCount < 2 || isRunning"
      @click="emit('runAlgorithm')"
    >
      {{ isRunning ? "⏳ Running…" : runButtonLabel }}
    </button>
  </div>
</template>

<style scoped>
.run-status-panel {
  padding: 0.75rem;
  background: var(--panel-bg-secondary, #f8fafc);
  border: 1px solid var(--panel-border, #e2e8f0);
  border-radius: 0.375rem;
  font-size: 0.8125rem;
}

.run-status-panel-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.run-status-panel-title {
  font-weight: 600;
  color: var(--panel-text-primary, #1a1a1a);
}

.run-status-panel-progress {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.run-status-panel-progress-bar {
  flex: 1;
  height: 6px;
  background: #e2e8f0;
  border-radius: 3px;
  overflow: hidden;
}

.run-status-panel-progress-fill {
  height: 100%;
  background: #3b82f6;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.run-status-panel-step-label {
  font-size: 0.75rem;
  color: var(--panel-text-muted, #64748b);
  white-space: nowrap;
}

.run-status-panel-current-op {
  color: var(--panel-text-primary, #1a1a1a);
  margin-bottom: 0.25rem;
}

.run-status-panel-remaining {
  color: var(--panel-text-muted, #64748b);
}
</style>
